import React, { forwardRef, ReactNode } from 'react';

import { useClassnames } from '@lumapps/classnames';

import './index.scss';

export interface GridLayoutSectionProps extends React.HTMLAttributes<HTMLElement> {
    /** which HTML element will be used for displaying the section */
    elementAs?: string;
    /** possible positions for the section */
    position: 'left' | 'middle' | 'right';
}

const CLASSNAME = 'grid-layout';

/**
 * Section component to be used inside the GridLayout component.
 * @param GridLayoutSectionProps
 * @returns Section
 */
export const Section = forwardRef<HTMLElement, GridLayoutSectionProps>(
    ({ className, elementAs = 'section', position, ...props }, ref) => {
        const Component = elementAs as any;
        const { element } = useClassnames(CLASSNAME);

        return <Component {...props} className={element(position, [className])} ref={ref} />;
    },
);

/**
 * Header component to be used inside the GridLayout component.
 */
export const Header: React.FC<React.HTMLAttributes<HTMLElement>> = ({ className, ...props }) => {
    const { element } = useClassnames(CLASSNAME);

    return <header {...props} className={element('header', [className])} />;
};

export interface GridLayoutProps {
    /** custom className */
    className?: string;
    children?: ReactNode;
}

/**
 * Displays components in a grid fashion, with the possibility to display up to 4 components in the following layout:
 * @example
 * <GridLayout>
 *     <Header>
 *     <Section position="left" />
 *     <Section position="middle" />
 *     <Section position="right" />
 * </GridLayout>
 *
 * This will display the header at the top, and in a grid fashion the components with position left, middle and right
 * in that order
 *
 * @family Layouts
 * @param GridLayoutProps
 * @returns GridLayout
 */
export const GridLayout: React.FC<GridLayoutProps> = ({ children, className, ...props }) => {
    const { element } = useClassnames(CLASSNAME);

    return (
        <section className={element('content', [className])} {...props}>
            {children}
        </section>
    );
};
